import { Injectable, signal } from '@angular/core';
import { GeoCoordinates } from '../utils/geo-coordinates';

export type UserLocationStatus = 'idle' | 'prompting' | 'granted' | 'denied' | 'unavailable';

export interface UserCoordinates extends GeoCoordinates {
  accuracy?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserGeolocationService {
  readonly status = signal<UserLocationStatus>('idle');
  readonly position = signal<UserCoordinates | null>(null);

  private watchId: number | null = null;
  private inFlight = false;

  ensureLocated(): void {
    if (!this.isSupported()) {
      this.status.set('unavailable');
      return;
    }
    if (this.status() === 'denied') {
      return;
    }
    if (this.status() === 'granted' && this.position()) {
      this.startWatch();
      return;
    }
    this.requestPosition(false);
  }

  requestPosition(force = false): void {
    if (!this.isSupported()) {
      this.status.set('unavailable');
      return;
    }
    if (this.inFlight) {
      return;
    }
    if (!force && this.status() === 'denied') {
      return;
    }

    this.inFlight = true;
    this.status.set('prompting');
    navigator.geolocation.getCurrentPosition(
      (result) => this.onPosition(result),
      (error) => this.onError(error),
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 60_000,
      },
    );
  }

  private onPosition(result: GeolocationPosition): void {
    this.inFlight = false;
    this.status.set('granted');
    this.position.set({
      lat: result.coords.latitude,
      lng: result.coords.longitude,
      accuracy: result.coords.accuracy,
    });
    this.startWatch();
  }

  private onError(error: GeolocationPositionError): void {
    this.inFlight = false;
    if (error.code === error.PERMISSION_DENIED) {
      this.status.set('denied');
      return;
    }
    if (this.position()) {
      this.status.set('granted');
      return;
    }
    this.status.set('unavailable');
  }

  private startWatch(): void {
    if (this.watchId != null || !this.isSupported()) {
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      (result) => {
        this.status.set('granted');
        this.position.set({
          lat: result.coords.latitude,
          lng: result.coords.longitude,
          accuracy: result.coords.accuracy,
        });
      },
      () => {
        // Keep the last known point if the watch fails.
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 30_000,
      },
    );
  }

  private isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.geolocation &&
      (typeof window === 'undefined' || window.isSecureContext !== false)
    );
  }
}
